import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Stethoscope, Users } from "lucide-react";
import { useSession } from "@/lib/session";
import { api } from "@/lib/api";
import { Button } from "../components/ui/button";

const ROLES = [
  {
    key: "student",
    label: "I'm a Student",
    sub: "Trace, play, and earn stars",
    Icon: GraduationCap,
    color: "bg-orange-500 hover:bg-orange-600 text-white",
    testId: "role-student-btn",
  },
  {
    key: "therapist",
    label: "I'm a Therapist",
    sub: "Assign work and monitor progress",
    Icon: Stethoscope,
    color: "bg-sky-600 hover:bg-sky-700 text-white",
    testId: "role-therapist-btn",
  },
  {
    key: "parent",
    label: "I'm a Parent",
    sub: "Cheer on your child's journey",
    Icon: Users,
    color: "bg-emerald-600 hover:bg-emerald-700 text-white",
    testId: "role-parent-btn",
  },
];

export default function RoleSwitcher() {
  const navigate = useNavigate();
  const { setRole, setUserId } = useSession();
  const [counts, setCounts] = useState({ students: 0, exercises: 0 });

  useEffect(() => {
    Promise.all([api.listUsers("student"), api.listExercises()])
      .then(([students, exercises]) => setCounts({ students: students.length, exercises: exercises.length }))
      .catch(() => {});
  }, []);

  const pick = async (key) => {
    setRole(key);
    if (key === "student") {
      navigate("/student");
    } else if (key === "therapist") {
      const therapists = await api.listUsers("therapist");
      if (therapists[0]) setUserId(therapists[0].id);
      navigate("/therapist");
    } else {
      const parents = await api.listUsers("parent");
      if (parents[0]) setUserId(parents[0].id);
      navigate("/parent");
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-orange-200/40 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-sky-200/40 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-6 py-16 lg:py-24">
        <div className="flex items-center gap-3 mb-12">
          <div className="h-10 w-10 rounded-2xl bg-orange-500 grid place-items-center text-white font-black">T</div>
          <div>
            <div className="font-bold text-lg leading-none" style={{ fontFamily: "Outfit" }}>TheraTrace</div>
            <div className="text-xs text-muted-foreground">Therapeutic handwriting for every learner</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3 space-y-6">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]"
                style={{ fontFamily: "Outfit" }}>
              A calmer way to <span className="text-orange-500">learn handwriting</span>.
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl">
              Engaging animated tracing exercises paired with powerful progress tracking, giving therapists and parents real-time insight into a student&apos;s handwriting growth.
            </p>
            <div className="flex flex-wrap gap-6 pt-2 text-sm text-muted-foreground">
              <div data-testid="stat-students"><b className="text-foreground text-2xl mr-1">{counts.students}</b>students</div>
              <div data-testid="stat-exercises"><b className="text-foreground text-2xl mr-1">{counts.exercises}</b>exercises</div>
              <div><b className="text-foreground text-2xl mr-1">4</b>levels</div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Continue as</div>
            {ROLES.map(({ key, label, sub, Icon, color, testId }) => (
              <button
                key={key}
                data-testid={testId}
                onClick={() => pick(key)}
                className={`w-full group rounded-3xl p-5 ${color} text-left flex items-center gap-4 min-h-[88px] transition-transform hover:-translate-y-0.5 active:translate-y-0`}
              >
                <div className="h-12 w-12 rounded-2xl bg-white/20 grid place-items-center">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-xl">{label}</div>
                  <div className="text-sm text-white/80">{sub}</div>
                </div>
                <div className="opacity-60 group-hover:opacity-100 transition">→</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
